import { CircularProgress } from '@mui/material';
import { deeplinks } from '@teamexos/fit-shared';
import React, { useState } from 'react';
import styled from 'styled-components';
import useTracking from '../../hooks/useTracking';
import { BORDER_GRAY } from '../../theme';
import { deeplinkBaseUrl } from '../../utils/deeplinks';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../utils/trackingEvents';
import LoadingPanel from '../LoadingPanel';
import Modal from '../Modal';
import { AppliedFilters } from '../reusable/AppliedFilters';
import Button, { ButtonSize, ButtonVariant } from '../reusable/Button';
import { LinkButton } from '../reusable/Button/LinkButton';
import { DataGrid } from '../reusable/DataGrid';
import { Divider } from '../reusable/Divider';
import GenericError from '../reusable/GenericError';
import LoadMore from '../reusable/LoadMore';
import PaginationWaypoint from '../reusable/PaginationWaypoint';
import { SearchField } from '../reusable/SearchField';
import { Tab, TabPanel, Tabs } from '../reusable/Tabs';
import Text, { TextKind } from '../reusable/Text';
import { SizeProp } from '../reusable/util';
import { ContentLibraryFilters } from './ContentLibraryFilters';
import { ContentLibraryList } from './ContentLibraryList';
import {
  ContentLibraryFilterProvider,
  useContentLibraryFilterContext,
} from './FilterContext';
import { PreviewModal } from './PreviewModal';
import { useContentsQuery } from './useContentsQuery';

const StyledModalContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const StyledModalContentContainer = styled.div`
  display: flex;
  padding: 0;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
`;
const StyledModalContent = styled.div`
  flex: 1;

  padding-bottom: 16px;

  overflow-y: auto;
  max-height: 100%;
`;
const StyledModalSidebar = styled.div`
  flex: 33% 0 0;
  display: flex;
  gap: 16px;
  flex-direction: column;

  padding-bottom: 16px;

  overflow-y: auto;
  max-height: 100%;
`;

const TabsContainer = styled.div`
  padding: 0 16px;
  box-shadow: 0px -1px 0px 0px ${BORDER_GRAY} inset;
`;

export enum LibraryTabs {
  OnDemand = 'On Demand',
  ScreenLinks = 'Screen Links',
}

interface Props {
  isOpen: boolean;
  onClose: (url?: string) => void;
  enabledTabs?: LibraryTabs[];
}

const ContentLibraryModalView: React.FC<Props> = ({
  isOpen,
  onClose,
  enabledTabs = Object.values(LibraryTabs),
}) => {
  const { track } = useTracking();
  const {
    options,
    appliedFilters,
    setAppliedFilters,
    toggleFilter,
    setSearch,
  } = useContentLibraryFilterContext();
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const {
    loading,
    error,
    isFetchingMore,
    filteredCount,
    totalCount,
    loadMore,
    fetchMoreError,
    setFetchMoreError,
    data: contentsData,
    hasNextPage,
  } = useContentsQuery({ isOpen });

  const handleTitleSearch = (name: string) => {
    setSearch((prev) => ({ ...prev, title: name }));
  };

  const handleSearchClear = () => {
    setSearch((prev) => ({ ...prev, title: undefined }));
  };

  const previewContent = (id: string) => {
    setPreviewId(id);
    setPreviewIsOpen(true);
  };

  const resetFilters = () => {
    setAppliedFilters([]);
    setSearch((prev) => ({ ...prev, title: undefined }));
  };

  const handleResetFilterClick = () => {
    resetFilters();
    track(EventNames.ResetAll, {
      card_name: EventProfileCardNames.ContentLibraryModal,
      event_category: EventCategories.Content,
      event_subcategory: EventSubCategories.Filters,
    });
  };

  const handleClose = (id?: string) => {
    resetFilters();
    onClose(id);
  };

  const onNavigationTabChange = (tab: number) => {
    setActiveTab(tab);
    track(EventNames.NavTabClick, {
      event_category: EventCategories.Navigation,
      card_name: EventProfileCardNames.ContentLibraryModal,
      event_label: enabledTabs[tab],
    });
  };

  const selectOnDemandItem = (
    id: string,
    subCategory?: EventSubCategories,
    cardName?: EventProfileCardNames,
  ) => {
    handleClose(id ? `https://fit.teamexos.com/ondemand/${id}` : undefined);
    setPreviewIsOpen(false);
    track(EventNames.ButtonClick, {
      card_name: cardName ?? EventProfileCardNames.ContentLibraryModal,
      event_category: EventCategories.Content,
      event_subcategory: subCategory,
      content_id: id,
      event_label: EventLabels.Select,
    });
  };

  const selectScreenLinkItem = (path: string) => {
    handleClose(`${deeplinkBaseUrl}${path}`);
    track(EventNames.ButtonClick, {
      card_name: EventProfileCardNames.ContentLibraryModal,
      event_category: EventCategories.ScreenLink,
      link_path: path,
      event_label: EventLabels.Select,
    });
  };

  const closeModal = () => {
    handleClose();
    track(EventNames.CloseClick, {
      card_name: EventProfileCardNames.ContentLibraryModal,
      event_category: EventCategories.Content,
    });
  };

  const handlePagination = () => {
    setFetchMoreError(undefined);
    const eventName = fetchMoreError
      ? EventNames.RetryClick
      : EventNames.LoadMoreData;
    track(eventName, {
      event_category: EventCategories.Content,
      card_name: EventProfileCardNames.ContentLibraryModal,
    });
    loadMore();
  };
  const expandedDeeplinks = deeplinks.flatMap((link) => {
    if (link.children) {
      // Include the parent link and all its children
      return [link, ...link.children];
    }
    return link;
  });

  const filteredDeeplinks = expandedDeeplinks.filter((link) => {
    return link.coachHubShow;
  });

  const formattedDeeplinks = filteredDeeplinks.map((deeplink) => ({
    ...deeplink,
    id: deeplink.route ?? 'root',
  }));

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        width="900px"
        trackingData={{
          card_name: EventProfileCardNames.ContentLibraryModal,
          event_category: EventCategories.Content,
        }}
      >
        <Modal.Header onClose={closeModal} closeLabel="Close content library">
          <Text kind={TextKind.TitleM}>Content Library</Text>
        </Modal.Header>
        <TabsContainer>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => onNavigationTabChange(newValue)}
          >
            {enabledTabs.map((tab, index) => (
              <Tab index={index} key={tab} label={tab} tabs={tab} />
            ))}
          </Tabs>
        </TabsContainer>
        <TabPanel
          tabs={LibraryTabs.OnDemand}
          value={activeTab}
          index={enabledTabs.indexOf(LibraryTabs.OnDemand)}
          conditionalRendering={false}
        >
          <Modal.Content style={{ paddingBottom: 0 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                gap: '16px',
                height: '70vh',
              }}
            >
              <StyledModalContentHeader>
                <div>
                  <Text kind={TextKind.BodyM}>
                    {loading && <CircularProgress size={12} />}
                    {!error && !loading && (
                      <>
                        {filteredCount} out of {totalCount} contents
                      </>
                    )}
                  </Text>
                </div>
                <div>
                  <SearchField
                    trackingData={{
                      card_name: EventProfileCardNames.ContentLibraryModal,
                      event_category: EventCategories.Content,
                    }}
                    onClear={handleSearchClear}
                    onSearch={handleTitleSearch}
                    placeholder="Search content"
                    testId="SearchContent"
                  />
                </div>
              </StyledModalContentHeader>

              <StyledModalContentContainer>
                <StyledModalSidebar>
                  <ContentLibraryFilters
                    trackingData={{
                      card_name: EventProfileCardNames.ContentLibraryModal,
                      event_category: EventCategories.Content,
                      event_subcategory: EventSubCategories.Filters,
                    }}
                  />
                </StyledModalSidebar>

                <StyledModalContent>
                  {appliedFilters.all.length > 0 && (
                    <>
                      <AppliedFilters
                        size={SizeProp.Small}
                        allFilters={options.all}
                        appliedFilters={appliedFilters.all.map(
                          (filter) => filter.value,
                        )}
                        clearAllFilters={resetFilters}
                        name="contentLibrary"
                        removeFilter={({ value }) => toggleFilter(value)}
                        trackingData={{
                          card_name: EventProfileCardNames.ContentLibraryModal,
                          event_category: EventCategories.Content,
                          event_subcategory: EventSubCategories.Filters,
                        }}
                      />
                      <Divider $marginBottom={8} $marginTop={8} />
                    </>
                  )}
                  {loading && (
                    <LoadingPanel
                      fullHeight
                      noBorder
                      testId="contentLibraryModalLoading"
                      backgroundColor="transparent"
                    />
                  )}
                  {!loading && error && (
                    <GenericError
                      testId="contentLibraryModalError"
                      error={error}
                    />
                  )}
                  {!loading && contentsData?.contents.edges.length === 0 && (
                    <div style={{ textAlign: 'center', marginTop: 48 }}>
                      <Text kind={TextKind.BodyM}>No contents found.</Text>
                      <LinkButton onClick={handleResetFilterClick}>
                        <Text kind={TextKind.BodyM}>Reset all filters</Text>
                      </LinkButton>
                    </div>
                  )}
                  {!loading && contentsData && (
                    <ContentLibraryList
                      contentsData={contentsData}
                      previewContent={previewContent}
                      selectItem={selectOnDemandItem}
                    />
                  )}
                  {!loading && hasNextPage && (
                    <>
                      <LoadMore
                        loadMore={handlePagination}
                        isFetchingMore={isFetchingMore}
                        error={fetchMoreError}
                      />
                      {!isFetchingMore &&
                        !fetchMoreError &&
                        activeTab ===
                          enabledTabs.indexOf(LibraryTabs.OnDemand) && (
                          <PaginationWaypoint callback={handlePagination} />
                        )}
                    </>
                  )}
                </StyledModalContent>
              </StyledModalContentContainer>
            </div>
          </Modal.Content>
        </TabPanel>
        {enabledTabs.includes(LibraryTabs.ScreenLinks) && (
          <TabPanel
            tabs={LibraryTabs.ScreenLinks}
            value={activeTab}
            index={enabledTabs.indexOf(LibraryTabs.ScreenLinks)}
            conditionalRendering
          >
            <Modal.Content>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  gap: '16px',
                  maxHeight: '70vh',
                }}
              >
                <DataGrid
                  density="comfortable"
                  sx={{
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  initialState={{
                    pagination: { rowCount: 100 },
                    sorting: { sortModel: [{ field: 'title', sort: 'asc' }] },
                  }}
                  rows={formattedDeeplinks}
                  hideFooter
                  columns={[
                    {
                      field: 'title',
                      headerName: 'Title',
                      flex: 1,
                    },
                    {
                      field: 'description',
                      headerName: 'Description',
                      flex: 2,
                    },
                    {
                      field: 'web',
                      headerName: 'Supported on Web',
                      flex: 1,
                      type: 'boolean',
                    },
                    {
                      headerName: '',
                      field: 'id',
                      sortable: false,
                      filterable: false,
                      renderCell: (params) => (
                        <Button
                          size={ButtonSize.ExtraSmall}
                          variant={ButtonVariant.Dark}
                          testId={`copy-screen-link-${params.row.route}`}
                          onClick={() => selectScreenLinkItem(params.row.route)}
                        >
                          Select
                        </Button>
                      ),
                    },
                  ]}
                />
              </div>
            </Modal.Content>
          </TabPanel>
        )}

        <Modal.Actions
          confirmButton={{
            label: 'Done',
            onClick: closeModal,
          }}
        />
      </Modal>
      <PreviewModal
        isOpen={previewIsOpen}
        onClose={() => setPreviewIsOpen(false)}
        onSelect={selectOnDemandItem}
        id={previewId}
      />
    </>
  );
};

export const ContentLibraryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  enabledTabs,
}) => (
  <ContentLibraryFilterProvider>
    <ContentLibraryModalView
      isOpen={isOpen}
      onClose={onClose}
      enabledTabs={enabledTabs}
    />
  </ContentLibraryFilterProvider>
);
